import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Zap, GitCommit } from 'lucide-react';
import axios from 'axios';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const FALLBACK_VERSIONS = [
  {
    version: "3.9.32", date: "Jun 2026", type: "FEATURE", latest: true,
    title: "PER-COIN HIGH GUARD TOGGLE",
    changes: [
      "Click a coin's 60D HIGH badge to toggle between BUY BLOCKED and HIGH BUY ALLOWED for that coin.",
    ],
  },
  {
    version: "3.9.31", date: "Jun 2026", type: "FEATURE", latest: false,
    title: "60-DAY HIGH BUY GUARDRAIL + NO 30-DAY SHUTOFF",
    changes: [
      "All automatic buys (RSI, DCA, EMA, Grid) are skipped when price is within 10% of the coin's 60-day high. Configurable in Settings → Protection.",
      "Dashboard shows each coin's 60D high, buy ceiling and a BUY BLOCKED badge.",
      "Removed the 30-day auto-termination — trading runs until you press STOP.",
    ],
  },
  {
    version: "3.9.30", date: "Feb 7, 2026", type: "UX", latest: false,
    title: "START-TIMER REMOVED — INSTANT TRADING",
    changes: [
      "Removed the 230s RSI_STABILIZATION skip in the trading loop and the 30s countdown splash screen. Trades can fire the instant you hit START.",
    ],
  },
  {
    version: "3.9.29", date: "Feb 7, 2026", type: "FIX", latest: false,
    title: "DEFAULT ACTIVE PAIRS = BTC-USD FOR EVERYONE",
    changes: [
      "Startup migration defaults active_pairs=['BTC-USD'] for existing users whose selection was missing/null/empty. Idempotent — leaves users with existing choices untouched.",
      "New users unchanged — they already default to BTC-USD.",
    ],
  },
  {
    version: "3.9.28", date: "Feb 7, 2026", type: "FIX", latest: false,
    title: "ARBITRAGE SCANNER — CRYPTO.COM USD (NOT USDT)",
    changes: [
      "USER: my app shows crypto.com BTC at $86.5k but their site shows $87.7k.",
      "ROOT CAUSE: arbitrage scanner was hitting crypto.com's BTC_USDT (Tether) instrument instead of BTC_USD.",
      "FIX: PAIR_MAPPINGS['crypto_com'] now maps every pair to its _USD variant — matches crypto.com's own API within $5.",
    ],
  },
  {
    version: "3.9.27", date: "Feb 7, 2026", type: "FIX", latest: false,
    title: "REAL-TIME COINBASE PRICE + SOURCE LABEL",
    changes: [
      "USER: BTC price shows $1000 lower than crypto.com. TRUTH: BTC legitimately trades at different prices on different exchanges — your bot must show Coinbase price because that's where it fills.",
      "UPGRADE: Switched from v2 /prices/spot (cached ~30s) to Coinbase Exchange /products/{pair}/ticker (real-time orderbook).",
      "UI: Added 'Coinbase spot · may differ ~1% from other exchanges' label to the BTC/USD card.",
    ],
  },
  {
    version: "3.9.26", date: "Feb 7, 2026", type: "CRITICAL", latest: false,
    title: "MUSIC UPLOAD CORRUPTION FIX (IDEMPOTENT CHUNKS)",
    changes: [
      "USER REPORTED: first two files work but the third music upload corrupts on playback, skipping entire sections.",
      "ROOT CAUSE: chunk uploads used append mode. Silent browser/proxy retries appended the same chunk TWICE, inserting duplicated audio into the middle of the file. Probabilistic — that's why only some files hit it.",
      "FIX: each chunk is now written to its own idempotent file and assembled in index order on finalize. Gap detection raises a clear error and cleans state so retry is safe.",
    ],
  },
  {
    version: "3.9.25", date: "Feb 7, 2026", type: "CRITICAL", latest: false,
    title: "TAKE_PROFIT PER-LOT THRESHOLD BUG FIX",
    changes: [
      "USER REPORTED: 8 lots at different prices — when lot #1 hit its take-profit target, the bot dumped all 8 lots at that same price, leaving profit on the table.",
      "ROOT CAUSE: The v3.9.17 lot filter used `lot_net_profit > 0` instead of `lot_gain_pct >= take_profit_percent`.",
      "FIX: Each lot now evaluated INDEPENDENTLY — only sells if its own gain % meets the take-profit threshold AND net-profit-after-fees > 0.",
    ],
  },
  {
    version: "3.9.24", date: "Feb 7, 2026", type: "FIX", latest: false,
    title: "USER COUNT SELF-HEAL",
    changes: [
      "USERS badge shows '… USERS' while loading (no more spurious '0 USERS' on first paint).",
      "Auto-retries /api/v2/auth/users up to 3 times with backoff on transient failure.",
    ],
  },
  {
    version: "3.9.23", date: "Feb 7, 2026", type: "FEATURE", latest: false,
    title: "SKIM MILESTONE CELEBRATION TOAST",
    changes: [
      "Real-time toast fires on every skim milestone event: '🎉 SKIM MILESTONE [LIVE] · 3 milestones paid · $15 → cold-wallet (BTC)'.",
      "Tags [LIVE] vs [SIM] so you know whether real funds moved.",
      "Warns if the live Coinbase transfer failed (counter still updates).",
      "Multi-tab safe via timestamp-based toast ID.",
    ],
  },
  {
    version: "3.9.22", date: "Feb 7, 2026", type: "FEATURE", latest: false,
    title: "CUMULATIVE SKIM ON BIG WINS",
    changes: [
      "PROPORTIONAL PAYOUT: A single $10k win with a $100 trigger + $5 transfer now pays 100 × $5 = $500 in one aggregated event (was: one $5 no matter what).",
      "EFFICIENT: One aggregated Coinbase send + one stats increment + one history row per event with milestones_paid / from → to / per_milestone_amount fields.",
      "SAFE: Continuous incremental sells still only pay NEW crossings (no double-payment).",
    ],
  },
  {
    version: "3.9.21", date: "Feb 7, 2026", type: "CRITICAL", latest: false,
    title: "SKIM ACCOUNTING + LIVE COINBASE TRANSFER",
    changes: [
      "ROOT CAUSE: session.add_skim() only updated in-memory state; the trading loop wrote to the wrong collection, so Session/Lifetime Skim stayed $0 forever.",
      "NEW: services/skim_service.maybe_execute_skim() — single source of truth called from trading loop AND manual /sell-lot. Increments user_skim_stats and writes user_skim_history rows.",
      "NEW: Live-mode Coinbase transfers. Converts USD transfer amount to configured skim_currency (default BTC) at market price and calls Coinbase send API. Records tx_id + errors on history row.",
      "FIX: Manual /sell-lot now feeds session.add_realized_profit so manual sells trigger the skim engine.",
    ],
  },
  {
    version: "3.9.20", date: "Feb 7, 2026", type: "UI", latest: false,
    title: "SKIM MOVED TO ADMIN PANEL",
    changes: [
      "The Skim card is no longer on the main dashboard.",
      "It now lives inside Admin Panel → SKIM tab with a Recent Transfers history.",
      "Auto-refreshes every 15s.",
    ],
  },
  {
    version: "3.9.19", date: "Feb 7, 2026", type: "FEATURE", latest: false,
    title: "TRADE TIMELINE / REPLAY",
    changes: [
      "NEW: /timeline page — scrub through any timeframe (1H → 30D) and watch every BUY/SELL replay against the live price chart.",
      "Play / Pause controls auto-advance the playhead; the slider lets you drag directly to any moment.",
      "Green dots = BUYs, red dots = SELLs. Tooltip shows trade amount, price, trigger, and P/L.",
      "Realized P/L updates in real time as trades are revealed.",
    ],
  },
  {
    version: "3.9.18", date: "Feb 7, 2026", type: "CRITICAL", latest: false,
    title: "MANUAL SELL LOT-CORRUPTION FIX (ID-AWARE FIFO EVERYWHERE)",
    changes: [
      "ROOT CAUSE: Manual sells were saved without a consumed_lot_ids tag. On FIFO replay, the sold amount was subtracted from the OLDEST lot instead of the one you clicked — corrupting the remaining lots' displayed buy prices.",
      "FIX: /user/bot/sell-lot now records the exact lot_id being sold; /user/bot/cost-basis honors consumed_lot_ids so UI + auto-bot + manual sells share the same lot ordering.",
      "FIX: Session buy_lots are removed by lot_id (no partial FIFO shrinking) on manual sells.",
      "FIX: Cost Basis dust filter drops sub-$1 residue lots matching session-restore logic.",
    ],
  },
  {
    version: "3.9.17", date: "Feb 6, 2026", type: "MAJOR", latest: false,
    title: "iOS LOGIN FIX: DUAL AUTH (COOKIE + BEARER TOKEN)",
    changes: [
      "ROOT CAUSE: iOS Safari's ITP silently drops cross-site cookies during Google OAuth redirects. That's why login was flaky on iOS.",
      "FIX: Backend now accepts session tokens via Cookie, Authorization: Bearer, and X-Session-Token.",
      "FIX: Frontend stores the token in localStorage after login and attaches it as Bearer on every request.",
      "iOS PWA / Safari private mode / cross-domain redirects can no longer break login. Emergency access backup remains but should never be needed.",
    ],
  },
  {
    version: "3.9.16", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "HARDENED REDIRECT: SERVICE WORKER LAYER (CACHE-IMMUNE)",
    changes: [
      "Service worker now enforces preview→production redirect at the SW layer, immune to HTML cache.",
      "Navigation requests are network-first so users always get the latest redirect logic.",
      "Cache purge on activate wipes stale HTML on every version bump.",
      "?preview=1 bypass syncs across HTML script + SW via postMessage.",
    ],
  },
  {
    version: "3.9.15", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "AUTO-REDIRECT PREVIEW VISITORS TO PRODUCTION",
    changes: [
      "Anyone visiting the preview URL now gets instantly redirected to https://xbitbotx.com.",
      "Developer bypass: ?preview=1 sets a localStorage flag to disable the redirect. Clear with ?preview=0.",
      "localhost is never redirected. Path/query/hash preserved on redirect.",
    ],
  },
  {
    version: "3.9.14", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "ADSENSE COMPLIANCE: CONTACT, DISCLAIMER, COOKIE POLICY PAGES",
    changes: [
      "NEW: /contact page — contact form with mailto fallback and SLA expectations",
      "NEW: /disclaimer page — full risk disclosure, not-financial-advice notice, software limitations",
      "NEW: /cookies page — cookie policy detailing every cookie with opt-out instructions",
      "Footer links updated everywhere to include the new pages",
      "All content pages remain public so AdSense crawlers can index them",
    ],
  },
  {
    version: "3.9.13", date: "Feb 6, 2026", type: "MINOR", latest: false,
    title: "RESET @ AVG BUY + UNDO + GOD-ONLY DIAG",
    changes: [
      "Reset Buy Lots now defaults to BLENDED AVG BUY PRICE (preserves true cost basis).",
      "Two reset modes: 'RESET @ AVG BUY' (recommended) and 'RESET @ MARKET' (clean-slate).",
      "NEW: Existing Archives panel — one-click UNDO for any prior reset.",
      "Diag page and reset/restore endpoints are now GOD-only.",
    ],
  },
  {
    version: "3.9.12", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "FIX: Cost Basis showing archived lots after reset",
    changes: [
      "Cost Basis modal now correctly excludes trades that were archived by a Reset Lots action.",
      "Before this fix, after a reset users would see the OLD lots + the NEW synthetic lot together (e.g. 7 instead of 1).",
    ],
  },
  {
    version: "3.9.11", date: "Feb 6, 2026", type: "MINOR", latest: false,
    title: "PERMANENT FIX: ID-AWARE LOT TRACKING (NO MORE FRAGMENTATION)",
    changes: [
      "Every buy lot now gets a unique lot_id. Each bot sell records consumed_lot_ids — the exact lots it sold.",
      "FIFO replay on bot restart removes lots by ID (whole-lot only), not by partial amount. Coinbase auto-sync no longer fragments cost basis.",
      "Untagged legacy sells fall back to FIFO with dust auto-cleanup (< $1 lots removed).",
      "RSI sells now preserve unsold lots instead of clearing the whole pair's buy_lots.",
      "NEW: Admin Reset endpoint — wipes fragmented history, creates one synthetic lot at current balance. Reversible.",
      "NEW: Reset Lots button on /diag/sell-audit page.",
    ],
  },
  {
    version: "3.9.10", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "CRITICAL: TAKE_PROFIT FEE BLEED FIX",
    changes: [
      "TAKE_PROFIT now consolidates ALL net-profitable lots into a SINGLE sell trade instead of selling one lot at a time on consecutive ticks",
      "Previously the bot would fire TAKE_PROFIT repeatedly, selling one $20 lot per tick = 5+ separate sell fees per round",
      "Now: one trigger fires → all net-profitable lots are bundled → ONE Coinbase order → ONE sell fee",
      "Still preserves the per-lot safety: lots that would lose money after fees are still skipped and held for recovery",
    ],
  },
  {
    version: "3.9.9", date: "Feb 6, 2026", type: "MINOR", latest: false,
    title: "CLICKABLE STATS TRADES + CONTENT PAGE LINKS",
    changes: [
      "Stats page trade rows are now clickable — opens the full Trade Detail modal with buy/sell price, fees, P/L, RSI, trigger, order ID, and per-lot breakdown (for protection sells)",
      "Implied cost basis shown on sell trades (computed from profit and amount)",
      "Per-lot breakdown section in the Trade Detail modal for protection sells",
      "Footer on Dashboard and Stats page now links to About, FAQ, Privacy, Terms, and Changelog (AdSense compliance pages)",
      "Refactored TradeDetailModal into a shared component (reused by Live Trade Feed and Stats trade table)",
    ],
  },
  {
    version: "3.9.8", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "TRADERS PAGE — MOBILE LAYOUT FIX",
    changes: [
      "Traders page now uses a stacked card layout on mobile (vertical aspect) — no more overlapping/overrunning text",
      "Each trader row on mobile shows: avatar + name + LIVE/IDLE on top, then 2x2 grid for Portfolio and Profit/Hr, then per-coin badges, then full-width COPY SETTINGS button",
      "Header collapses gracefully on narrow screens; legend wraps instead of overflowing",
      "Desktop layout (md and up) unchanged — still uses the full 12-column table",
    ],
  },
  {
    version: "3.9.7", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "NEW CASH REGISTER SELL SOUND",
    changes: [
      "Sell trades now play a real cash-register 'ka-ching!' audio file (user-uploaded) instead of the synthesized version",
      "Falls back to the synthesized cha-ching if the audio file fails to load (offline mode)",
      "Audio is preloaded on first user gesture so mobile browsers won't block playback",
    ],
  },
  {
    version: "3.9.6", date: "Feb 6, 2026", type: "PATCH", latest: false,
    title: "PER-LOT PROTECTION SELLS — STOPS PORTFOLIO BLEED",
    changes: [
      "TAKE_PROFIT and STOP_LOSS now evaluate each buy lot individually — no more grouped 'sell everything' on a single trigger",
      "TAKE_PROFIT only sells lots whose individual gain meets the threshold AND has positive net profit after 2.5% round-trip fees",
      "STOP_LOSS only sells lots whose individual drop hits the threshold — lots bought lower are preserved to recover",
      "Profit reporting is now accurate: P/L computed from blended cost basis of the actual lots sold (not stale avg_buy)",
      "Buy lots remaining after a partial protection sell stay tracked — no more lost cost basis on protection triggers",
      "NEW: Protection Sell Audit panel on dashboard — shows last TAKE_PROFIT/STOP_LOSS trade with per-lot breakdown (buy price, gain%, gross P/L, fee, net P/L)",
    ],
  },
  {
    version: "3.8.6", date: "May 5, 2026", type: "PATCH", latest: false,
    title: "CRITICAL: TRADING LOOP CRASH FIX",
    changes: [
      "Fixed trading loop crash: 'BuyLot object has no attribute get' — per-lot profit check now handles both BuyLot objects and dict format",
      "Fixed admin restart using wrong settings key — bots were always restarting in simulation mode",
      "These two bugs combined caused all restarted bots to crash immediately",
    ],
  },
  {
    version: "3.8.5", date: "May 5, 2026", type: "PATCH", latest: false,
    title: "REMOVED SYNC BUTTONS",
    changes: [
      "Removed SYNC and FULL SYNC buttons — auto-sync now handles exchange synchronization",
    ],
  },
  {
    version: "3.8.4", date: "May 5, 2026", type: "PATCH", latest: false,
    title: "EMAIL NOTIFICATION FIX",
    changes: [
      "Fixed nightly digest and trade emails — was calling Resend synchronously causing silent failures",
      "Unified email sending with async pattern and proper error logging",
      "Test-email endpoint now uses same send function as trade alerts",
    ],
  },
  {
    version: "3.9.5", date: "May 2, 2026", type: "PATCH", latest: false,
    title: "MOBILE INSTALL FIX AND TRADERS IN MENU",
    changes: [
      "Install App button works on iOS — shows step-by-step Safari Add to Home Screen guide",
      "Install App button on Android/Chrome triggers native install prompt or shows manual instructions",
      "Added TRADERS and HIGH SCORES to the mobile MENU dropdown",
    ],
  },
  {
    version: "3.8", date: "May 2, 2026", type: "MAJOR", latest: false,
    title: "MOBILE UI, PWA, AI ASSISTANT, DYNAMIC FEES, AND CHANGELOG",
    changes: [
      "Mobile-responsive navigation — collapsed 17+ buttons into two compact dropdown menus",
      "BitBot AI Assistant — floating chat bubble with context-aware answers about your portfolio and strategies",
      "Dynamic fee estimation — taker fees calculated per-user based on Coinbase 30-day volume tier",
      "Progressive Web App (PWA) — install BitBot on your home screen for native app experience",
      "Changelog page — every release, every feature, every fix with timeline UI",
      "What's New card — floating dismissible card for 7 days after each release",
      "New user alert — user count badge flashes red/green with ahooga horn",
      "Fixed mode badge showing SIMULATION instead of LIVE on Traders screen",
    ],
  },
  {
    version: "3.7", date: "April 24, 2026", type: "MINOR", latest: false,
    title: "AI ASSISTANT AND DYNAMIC FEES",
    changes: [
      "BitBot AI Assistant — context-aware chat powered by Gemini 2.5 Flash",
      "Dynamic fee estimation per-user based on Coinbase 30-day volume tier",
      "New user alert with ahooga horn and flashing badge",
      "Cash register cha-ching sound for sell trades",
    ],
  },
  {
    version: "3.6.5", date: "April 18, 2026", type: "MINOR", latest: false,
    title: "LOT HEALTH, TRADE DETAILS, AND EMAIL FIXES",
    changes: [
      "Lot Health View in Cost Basis modal — health bars showing distance to profitability per lot",
      "Trade Detail popup — click any trade in the feed to see price, fees, P/L, RSI, and trigger info",
      "Fixed email notifications not firing for TAKE_PROFIT live sells",
      "Fixed Coinbase adapter response parsing for SDK object responses",
      "Updated fee calculation to match actual Coinbase fees",
      "Cash register cha-ching sound for sell trades",
    ],
  },
  {
    version: "3.6.4", date: "April 15, 2026", type: "MINOR", latest: false,
    title: "LIVE TRADE FEED FIX AND MODE FILTERING",
    changes: [
      "Fixed Live Trade Feed only showing BUYs — now filters by current trading mode",
      "Added diagnostic endpoint for full BUY/SELL breakdown",
      "Buy/sell count stats shown in feed header",
    ],
  },
  {
    version: "3.6.3", date: "April 10, 2026", type: "MINOR", latest: false,
    title: "TELEGRAM, BADGES, AND NOTIFICATION FIXES",
    changes: [
      "Telegram notifications — trade alerts, bot stop, daily P/L summary, new badge earned",
      "Badge Showcase — pin up to 3 badges on the leaderboard",
      "Silenced simulation mode notifications",
      "Fixed Browser Push Notification toggles",
      "50% faster refresh intervals",
    ],
  },
  {
    version: "3.6.2", date: "April 8, 2026", type: "MINOR", latest: false,
    title: "PER-LOT TRADING AND SOUND FIXES",
    changes: [
      "Per-lot profit checking — bot evaluates each buy lot individually before selling",
      "Fixed bot selling all lots at once",
      "Fixed trade alerts showing wrong trigger",
      "Fixed Dashboard buy/sell sounds not playing",
    ],
  },
  {
    version: "3.6", date: "March 2026", type: "MAJOR", latest: false,
    title: "INITIAL RELEASE",
    changes: [
      "RSI-based automated trading with configurable thresholds",
      "Multi-coin support: BTC, ETH, SOL, LTC, DOGE, BCH, XRP, SHIB",
      "Simulation and Live trading modes with Coinbase integration",
      "Real-time WebSocket price broadcasting",
      "Cost basis tracking with FIFO lot management",
      "Protection sells: stop-loss, take-profit, trailing stop",
      "Email/SMS notifications, badge system, leaderboard",
    ],
  },
];

const typeBadge = (type, isLatest) => {
  const colors = {
    MAJOR: 'bg-red-900/60 text-red-400 border-red-700',
    MINOR: 'bg-blue-900/60 text-blue-400 border-blue-700',
    PATCH: 'bg-gray-800/60 text-gray-400 border-gray-600',
  };
  return (
    <span className="flex items-center gap-1.5">
      {isLatest && (
        <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 bg-[#003B00] text-[#00FF41] border border-[#00FF41]">
          LATEST
        </span>
      )}
      <span className={`text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 border ${colors[type] || colors.PATCH}`}>
        {type}
      </span>
    </span>
  );
};

export default function ChangelogPage() {
  const [versions, setVersions] = useState(FALLBACK_VERSIONS);
  const [current, setCurrent] = useState('3.8.6');
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_V2}/changelog`, { timeout: 5000 });
        if (res.data?.versions?.length) {
          setVersions(res.data.versions);
          setCurrent(res.data.current || '3.8.6');
        }
      } catch {
        // Use fallback data
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8"
          data-testid="changelog-back"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </button>

        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Tag className="h-8 w-8 text-red-500" />
            <h1 className="text-4xl font-bold">
              <span className="text-white">Change</span>
              <span className="text-[#00FF41]">log</span>
            </h1>
          </div>
          <p className="text-gray-400 text-base">
            Every release. Every feature. Every fix.{' '}
            You're currently on <span className="text-[#00FF41] font-bold">v{current}</span>
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-[18px] top-0 bottom-0 w-px bg-gradient-to-b from-[#00FF41] via-[#003B00] to-transparent" />

          <div className="space-y-10">
            {versions.map((v) => (
              <div key={v.version} className="relative pl-14" data-testid={`changelog-v${v.version}`}>
                <div className={`absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center border-2 ${
                  v.latest
                    ? 'bg-red-950 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.4)]'
                    : 'bg-[#0a0a0a] border-[#003B00]'
                }`}>
                  {v.latest ? (
                    <Zap className="h-4 w-4 text-red-400" />
                  ) : (
                    <GitCommit className="h-4 w-4 text-[#003B00]" />
                  )}
                </div>

                <div className={`border rounded p-5 ${
                  v.latest
                    ? 'border-red-800/50 bg-[#0a0a0a]'
                    : 'border-[#003B00]/30 bg-[#050505]'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold font-mono text-white">v{v.version}</span>
                      {typeBadge(v.type, v.latest)}
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-500 text-sm">
                      <Calendar className="h-3.5 w-3.5" />
                      {v.date}
                    </div>
                  </div>

                  <h3 className="text-sm text-gray-400 font-mono tracking-wider uppercase mb-4">
                    {v.title}
                  </h3>

                  <ul className="space-y-3">
                    {v.changes.map((change, ci) => (
                      <li key={ci} className="flex items-start gap-2.5 text-sm text-gray-300 leading-relaxed">
                        <span className="text-red-500 mt-1.5 shrink-0 text-[8px]">&#9679;</span>
                        {change}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
